import { StarIcon, PlusIcon } from "@skbkontur/icons";
import { ColumnStack, Fill, Fit, RowStack } from "@skbkontur/react-stack-layout";
import { Button, Input, Loader, Toggle, Tooltip, Toast } from "@skbkontur/react-ui";
import { ArrowRoundSyncForwardIcon } from "@skbkontur/icons";
import _ from "lodash";
import * as React from "react";
import { useDebounce } from "use-debounce";
import { useCallback } from "react";

import { useCandyApiClient } from "../../../../../Common/CandyApiClient/CandyApiClientContext";
import { Action, ActionType } from "../../Domain/Actions";
import { useJsonBin } from "../../Utils/Hooks/UseJsonBin";
import { useLocalStorage } from "../../../../../Common/Hooks/UseLocalStorage";
import { useFufUploader } from "../FufUploader/FufUploader";
import { GfvItem } from "../GfvItem/GfvItem";
import { RequisitesProvider } from "../RequisitesProvider/RequisitesProvider";
import { useAsyncEffect } from "../../Utils/Hooks/UseAsyncEffect";
import { ActionView } from "../ActionView/ActionView";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";

import cn from "./FormsHelperApplication.less";
import { GfvInfo } from "./GfvInfo";

const getReplacedLetters = (str: string | undefined) => {
    if (str === undefined) {
        return "";
    }
    const replacer: { [key: string]: string } = {
        q: "й",
        w: "ц",
        e: "у",
        r: "к",
        t: "е",
        y: "н",
        u: "г",
        i: "ш",
        o: "щ",
        p: "з",
        "[": "х",
        "]": "ъ",
        a: "ф",
        s: "ы",
        d: "в",
        f: "а",
        g: "п",
        h: "р",
        j: "о",
        k: "л",
        l: "д",
        ";": "ж",
        "'": "э",
        z: "я",
        x: "ч",
        c: "с",
        v: "м",
        b: "и",
        n: "т",
        m: "ь",
        ",": "б",
        ".": "ю",
        "/": ".",
    };
    return str.replace(/[A-z/,.;\'\]\[]/g, (letter: string): string => {
        if (letter === undefined) {
            return "";
        }
        if (letter === letter.toLowerCase()) {
            return replacer[letter] as string;
        } else {
            // @ts-ignore
            return replacer[letter.toLowerCase()].toUpperCase();
        }
    });
};
export function FormsHelperApplication(): JSX.Element {
    const api = useCandyApiClient();

    const [loading, setLoading] = React.useState(false);
    const [gfvs, setGfvs] = React.useState<string[]>();
    const [gfvInfos, setGfvInfos] = useJsonBin<{ [gfv: string]: GfvInfo }>("5eb6b8e3a47fdd6af1604655", {});
    const [searchText, setSearchText] = useLocalStorage<string>("searchText", "");
    const [searchTextDeferred] = useDebounce(searchText, 500);
    const [actions, setActions] = useLocalStorage<Action[]>("actions.v3", []);
    const [favoriteGfvs, setFavoriteGfvs] = useLocalStorage<string[]>("gfvFavorites", []);
    const [embeddedLogEnabled, setEmbeddedLogEnabled] = useLocalStorage<boolean>(
        "openOptions.embeddedLogEnabled",
        false
    );
    const [staffServiceEnabled, setStaffServiceEnabled] = useLocalStorage<boolean>(
        "openOptions.staffServiceEnabled",
        false
    );
    const [staffServiceEmployerId, setStaffServiceEmployerId] = useLocalStorage<string>(
        "openOptions.staffServiceEmployerId",
        "dba05ac2-cf96-4452-820e-7df85b7b709c"
    );
    const [scenarioEnabled, setScenarioEnabled] = useLocalStorage<boolean>("openOptions.scenarioEnabled", false);
    const [scenario, setScenario] = useLocalStorage<string>("openOptions.scenario", "");
    const [isReadOnlyEnabled, setIsReadOnlyEnabled] = useLocalStorage<boolean>("openOptions.isReadOnlyEnabled", false);
    const gfvsHash = React.useMemo(() => new Set(gfvs), [gfvs]);

    const addAction = (action: Action) => {
        setActions([action, ...actions.slice(0, 100)]);
    };

    const createEmptyDraft = async (gfv: string) => {
        try {
            const response = await api.createEmptyDraft({
                ns: "00000000-0000-0000-0000-000000000000",
                gfv: gfv,
                scenario: scenarioEnabled && scenario ? scenario : undefined,
            });
            window.open(getOpenEditorUrl(response.editorUrl), "_blank");
        } catch (e) {
            Toast.push("Ошибка при создании черновика");
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    const getOpenEditorUrl = useCallback(
        (url: string): string => {
            const parts: string[] = [];
            if (!url.includes("debug=true")) {
                parts.push("debug=true");
            }
            if (embeddedLogEnabled) {
                parts.push("embeddedLogEnabled=true");
            }
            if (staffServiceEnabled) {
                parts.push(`employerId=${staffServiceEmployerId}`);
            }
            if (isReadOnlyEnabled) {
                parts.push("isReadOnly=true");
            }
            return url + (url.includes("?") ? "&" : "?") + parts.join("&");
        },
        [embeddedLogEnabled, staffServiceEnabled, staffServiceEmployerId, isReadOnlyEnabled]
    );

    const getScenario = useCallback(
        () => (scenarioEnabled && scenario ? scenario : undefined),
        [scenarioEnabled, scenario]
    );

    const [uploadFuf, uploadFufElement] = useFufUploader(
        getOpenEditorUrl,
        info => {
            addAction({
                type: ActionType.UploadFuf,
                gfv: info.gfv,
                dateString: new Date().toString(),
                draft: info.draft,
                fufDriveUri: info.fufDriveUri,
                editorUrl: info.editorUrl,
            });
        },
        getScenario
    );

    useAsyncEffect(async (abortSignal: AbortSignal) => {
        setLoading(true);
        try {
            setGfvs(await api.getAllGfvs(abortSignal));
        } finally {
            setLoading(false);
        }
    }, []);

    const reloadAllGfvs = async () => {
        setLoading(true);
        try {
            setGfvs(await api.forceGetAllGfvs());
        } finally {
            setLoading(false);
        }
    };

    const currentFavoriteGfvs = favoriteGfvs.filter(x => gfvsHash.has(x));

    const filteredGfvs = React.useMemo(
        () =>
            (gfvs ?? []).filter(
                x =>
                    searchTextDeferred === "" ||
                    x.includes(searchTextDeferred) ||
                    gfvInfos[x]?.description?.toLowerCase().includes(searchTextDeferred.toLowerCase()) ||
                    gfvInfos[x]?.shortDescription.toLowerCase().includes(searchTextDeferred.toLowerCase()) ||
                    gfvInfos[x]?.description
                        ?.toLowerCase()
                        .includes(getReplacedLetters(searchTextDeferred).toLowerCase()) ||
                    gfvInfos[x]?.shortDescription
                        .toLowerCase()
                        .includes(getReplacedLetters(searchTextDeferred).toLowerCase())
            ),
        [gfvs, searchTextDeferred, gfvInfos]
    );

    const allGfvs = React.useMemo(
        () =>
            filteredGfvs.slice(0, 14 - currentFavoriteGfvs.length).map(gfv => (
                <Fit key={gfv}>
                    <GfvItem
                        gfvInfo={gfvInfos[gfv]}
                        onChangeGfvInfo={value => setGfvInfos({ ...gfvInfos, [gfv]: value })}
                        onGetOpenEditorUrl={getOpenEditorUrl}
                        gfv={gfv}
                        onAddAction={addAction}
                        onChangeIsFavorite={isFavorite => setGfvIsFavorite(gfv, isFavorite)}
                        isFavorite={favoriteGfvs.includes(gfv)}
                        getScenario={getScenario}
                    />
                </Fit>
            )),
        [filteredGfvs, favoriteGfvs, gfvInfos, getOpenEditorUrl, getScenario]
    );

    const setGfvIsFavorite = (gfv: string, isFavorite: boolean): void => {
        if (isFavorite) {
            setFavoriteGfvs(_.union(favoriteGfvs, [gfv]));
        } else {
            setFavoriteGfvs(_.difference(favoriteGfvs, [gfv]));
        }
    };

    const currentActions = React.useMemo(() => actions.filter(x => gfvsHash.has(x.gfv)), [actions, gfvsHash]);

    if (loading) {
        return <Loader active={true} />;
    }

    return (
        <RequisitesProvider>
            <div className={cn("app")}>
                {uploadFufElement}
                <>
                    <ColumnStack block stretch gap={4}>
                        <Fit>
                            <h1>Forms helper</h1>
                        </Fit>
                        <Fit style={{ marginLeft: -12 }}>
                            <Input
                                placeholder="Фильтр по GFV"
                                value={searchText}
                                onValueChange={setSearchText}
                                size="medium"
                                autoFocus
                                width="100%"
                            />
                            <div style={{ marginLeft: 12, marginTop: 4 }}>
                                <Button use="link" onClick={() => setSearchText("")}>
                                    Очистить строку поиска
                                </Button>
                            </div>
                        </Fit>
                        <Fit>
                            <RowStack gap={4} block>
                                <Fill>
                                    {gfvs && (
                                        <ColumnStack block gap={2} stretch>
                                            <Fit>
                                                <RowStack block gap={2} baseline>
                                                    <Fill>
                                                        <RowStack gap={2}>
                                                            <h2>GFVs</h2>
                                                            <Button
                                                                icon={<ArrowRoundSyncForwardIcon />}
                                                                onClick={reloadAllGfvs}>
                                                                Перезагрузить GFVs
                                                            </Button>
                                                        </RowStack>
                                                    </Fill>
                                                    <Fit>
                                                        <Button icon={<PlusIcon />} onClick={uploadFuf}>
                                                            Загрузить ФУФ
                                                        </Button>
                                                    </Fit>
                                                </RowStack>
                                            </Fit>
                                            {searchTextDeferred === "" && (
                                                <Fit>
                                                    <h3>Избранное</h3>
                                                    <ColumnStack stretch block gap={0} className={cn("gfv-list")}>
                                                        {currentFavoriteGfvs.length === 0 && (
                                                            <Fit className={cn("description")}>
                                                                Нет избранного. Чтобы добавить, нажмите <StarIcon />{" "}
                                                                рядом с GFV.
                                                            </Fit>
                                                        )}
                                                        {currentFavoriteGfvs.map(gfv => (
                                                            <Fit key={gfv}>
                                                                <GfvItem
                                                                    gfv={gfv}
                                                                    gfvInfo={gfvInfos[gfv]}
                                                                    onChangeGfvInfo={value =>
                                                                        setGfvInfos({ ...gfvInfos, [gfv]: value })
                                                                    }
                                                                    onGetOpenEditorUrl={getOpenEditorUrl}
                                                                    onChangeIsFavorite={isFavorite =>
                                                                        setGfvIsFavorite(gfv, isFavorite)
                                                                    }
                                                                    isFavorite={favoriteGfvs.includes(gfv)}
                                                                    onAddAction={addAction}
                                                                    getScenario={getScenario}
                                                                />
                                                            </Fit>
                                                        ))}
                                                    </ColumnStack>
                                                </Fit>
                                            )}
                                            <Fit>
                                                <h3>{searchTextDeferred === "" ? "Все GFV" : "Отфильтрованные GFV"}</h3>
                                                <ColumnStack stretch block gap={0} className={cn("gfv-list")}>
                                                    {allGfvs}
                                                </ColumnStack>
                                            </Fit>
                                        </ColumnStack>
                                    )}
                                </Fill>
                                <Fit>
                                    <ColumnStack stretch block gap={6} className={cn("last-actions")}>
                                        <Fit>
                                            <h2>Настройки</h2>
                                        </Fit>
                                        <Fit>
                                            <Toggle
                                                checked={embeddedLogEnabled}
                                                onValueChange={setEmbeddedLogEnabled}
                                            />{" "}
                                            Открывать с встроенным протоколом
                                        </Fit>
                                        <Fit>
                                            <Toggle
                                                checked={staffServiceEnabled}
                                                onValueChange={setStaffServiceEnabled}
                                            />{" "}
                                            Открывать c сервисом сотрудников
                                            {staffServiceEnabled && (
                                                <>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            marginTop: "8px",
                                                        }}>
                                                        <Tooltip
                                                            render={() => (
                                                                <span style={{ color: "#333" }}>
                                                                    Впиши сюда свой EmployeeId, или пользуйся готовым,
                                                                    если зашел под кацем
                                                                </span>
                                                            )}
                                                            pos="top center">
                                                            <Input
                                                                leftIcon={"?"}
                                                                value={staffServiceEmployerId}
                                                                onChange={e =>
                                                                    setStaffServiceEmployerId(e.target.value)
                                                                }
                                                                width={"100%"}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </>
                                            )}
                                        </Fit>
                                        <Fit>
                                            <Toggle checked={isReadOnlyEnabled} onValueChange={setIsReadOnlyEnabled} />{" "}
                                            Открывать в режиме ReadOnly
                                        </Fit>
                                        <Fit>
                                            <Toggle checked={scenarioEnabled} onValueChange={setScenarioEnabled} />{" "}
                                            Открывать c сценарием
                                            {scenarioEnabled && (
                                                <>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            marginTop: "8px",
                                                        }}>
                                                        <Tooltip
                                                            render={() => (
                                                                <span style={{ color: "#333" }}>
                                                                    Впиши сюда сценарий для создания черновика
                                                                </span>
                                                            )}
                                                            pos="top center">
                                                            <Input
                                                                leftIcon={"?"}
                                                                value={scenario}
                                                                onChange={e => setScenario(e.target.value)}
                                                                width={"100%"}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </>
                                            )}
                                        </Fit>

                                        <Fit>
                                            <h2 style={{ marginBottom: 8 }}>Недавние действия</h2>
                                            <ColumnStack block stretch gap={2}>
                                                {currentActions
                                                    .slice(0, 5)
                                                    .map((x, i) => (
                                                        <ActionView
                                                            key={i.toString()}
                                                            action={x}
                                                            onCreateEmptyDraft={createEmptyDraft}
                                                            onGetOpenEditorUrl={getOpenEditorUrl}
                                                        />
                                                    ))
                                                    .filter(isNotNullOrUndefined)}
                                            </ColumnStack>
                                        </Fit>
                                    </ColumnStack>
                                </Fit>
                            </RowStack>
                        </Fit>
                    </ColumnStack>
                </>
            </div>
        </RequisitesProvider>
    );
}
